import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components/Icon/Icon';
import { useSortableRows } from '../../hooks/useSortableRows';
import { Root, Hint, List, Row, Handle, Position, Titles, RowTitle, Tag, Empty } from './ReadingOrderList.styles';

export type ReadingOrderItem = {
  /** Empty for an article that has never been saved (it has no server id yet). */
  id: string;
  title: string;
  /** The article being edited, called out by the row's own styling. */
  current: boolean;
  /**
   * A "Start here" guide. A category may have SEVERAL (the service puts no
   * uniqueness constraint on the column), and they all sort ahead of the
   * ordinary articles, ordered among themselves by sequence.
   */
  pillar: boolean;
  /** Unpublished, so it is in the order but not yet on the public site. */
  draft: boolean;
};

export type ReadingOrderListProps = {
  /** In reading order already: every pillar first, then the rest. */
  items: ReadingOrderItem[];
  /**
   * The reordered list. A row dragged across the pillar seam comes back with its
   * `pillar` flipped, so the caller has to persist that alongside the order.
   */
  onChange: (items: ReadingOrderItem[]) => void;
};

/** React key AND focus identity of a row; an unsaved article has no id yet. */
const rowKey = (item: ReadingOrderItem): string => item.id || 'new-article';

/**
 * Drag-sortable reading order for one category: the sequence the public site
 * walks readers through the cluster in, edited as the list it actually produces
 * instead of as a number per article.
 *
 * Every row moves, including the "Start here" guides. The public ordering key is
 * `pillar, then sequence, then title`, so the pillars are simply the rows at the
 * head of the list, and there can be more than one of them. That makes position
 * and flag the same fact, and the list treats them as one: drag a row above the
 * last "Start here" guide and it becomes one, drag it back below and it stops
 * being one. The toggle beside this list is the same switch reached the other
 * way, for the article being edited.
 *
 * The gesture itself — pointer tracking, the gap easing open, the commit held
 * back until the released row has landed, Arrow Up/Down for the keyboard — lives
 * in `hooks/useSortableRows` and is shared with the SEO list builders. What stays
 * here is the only part that is about reading order: what a move MEANS.
 */
export const ReadingOrderList = ({ items, onChange }: ReadingOrderListProps): React.ReactElement => {
  const { t } = useTranslation();

  /**
   * Move a row, and let its new POSITION decide whether it is a "Start here"
   * guide, because on the public site that is all the flag means: pillars sort
   * ahead of everything else, so a row above the seam is one and a row below it
   * is not. Dropping exactly ON the seam changes nothing, so a zone change always
   * takes a deliberate drag past it rather than an ambiguous landing.
   */
  const onMove = React.useCallback(
    (from: number, to: number): void => {
      const next = items.slice();
      const [moved] = next.splice(from, 1);
      // Pillars occupy the head of the list, so counting them among the OTHER
      // rows gives exactly the index of the seam this drop is measured against.
      const seam = next.filter((item) => item.pillar).length;
      const pillar = to === seam ? moved.pillar : to < seam;
      next.splice(to, 0, { ...moved, pillar });
      onChange(next);
    },
    [items, onChange],
  );

  const keys = React.useMemo(() => items.map(rowKey), [items]);
  const { listRef, row, handleProps } = useSortableRows({ keys, onMove });

  if (items.length === 0) {
    return (
      <Root>
        <Empty text="Pick a category to set this article's place in its reading order." tag="span" />
      </Root>
    );
  }

  return (
    <Root>
      <Hint
        text="Drag to set the order readers walk through this category. Rows above the last Start here guide are Start here guides themselves."
        tag="span"
      />
      <List ref={listRef}>
        {items.map((item, index) => {
          const state = row(index);
          return (
            <Row
              key={rowKey(item)}
              $dragged={state.dragged}
              $shift={state.shift}
              $animated={state.animated}
              $current={item.current}
            >
              <Handle
                type="button"
                aria-label={t('Reorder {{title}}', { title: item.title || t('Untitled article') })}
                {...handleProps(index)}
              >
                <Icon name="drag-handle" size={18} />
              </Handle>
              <Position text={String(state.position + 1)} tag="span" $current={item.current} />
              <Titles>
                <RowTitle text={item.title || 'Untitled article'} tag="span" $current={item.current} />
                {item.pillar ? <Tag text="Start here" tag="span" $tone="pillar" /> : null}
                {item.draft ? <Tag text="Draft" tag="span" $tone="draft" /> : null}
              </Titles>
            </Row>
          );
        })}
      </List>
    </Root>
  );
};

export default ReadingOrderList;
